/**
 * Hybrid goal recommender (same idea as AI chat):
 * - Numbers from Home KPI snapshot + history (deterministic)
 * - Short Japanese "why" from those numbers (no baseless targets)
 * - No extra BigQuery; no full goals LLM agent
 */

import { resolveNewReturnFromHistory } from './kpiSnapshot';

function historyRow(kpiHistory, selectedMonth) {
  if (!Array.isArray(kpiHistory) || !selectedMonth) return null;
  return kpiHistory.find((r) => r.month === selectedMonth || r.ym === selectedMonth) || null;
}

function latestHistoryRow(kpiHistory) {
  if (!Array.isArray(kpiHistory) || kpiHistory.length === 0) return null;
  return kpiHistory[kpiHistory.length - 1];
}

export function resolveSalesSnapshot(kpis, kpiHistory, selectedMonth) {
  if (kpis?.total_sales != null && !Number.isNaN(Number(kpis.total_sales))) {
    return { value: Number(kpis.total_sales), month: selectedMonth, fromHistory: false };
  }
  const row = historyRow(kpiHistory, selectedMonth) || latestHistoryRow(kpiHistory);
  if (row?.total_sales != null) {
    return {
      value: Number(row.total_sales),
      month: row.month || selectedMonth,
      fromHistory: true,
    };
  }
  return null;
}

export function resolveUnitPriceSnapshot(kpis, kpiHistory, selectedMonth) {
  if (kpis?.total_sales != null && kpis?.customer_count) {
    const n = Number(kpis.customer_count);
    if (n > 0) {
      return {
        value: Math.round(Number(kpis.total_sales) / n),
        month: selectedMonth,
        fromHistory: false,
      };
    }
  }
  const row = historyRow(kpiHistory, selectedMonth) || latestHistoryRow(kpiHistory);
  if (row?.total_sales != null && row?.customer_count) {
    const n = Number(row.customer_count);
    if (n > 0) {
      return {
        value: Math.round(Number(row.total_sales) / n),
        month: row.month || selectedMonth,
        fromHistory: true,
      };
    }
  }
  // history may lack customer_count — derive from new+repeat if present
  if (row?.total_sales != null) {
    const n =
      (Number(row.new_customer_count) || 0) + (Number(row.repeat_customer_count) || 0);
    if (n > 0) {
      return {
        value: Math.round(Number(row.total_sales) / n),
        month: row.month || selectedMonth,
        fromHistory: true,
      };
    }
  }
  return null;
}

export function resolveNewReturnSnapshot(kpis, kpiHistory, selectedMonth) {
  if (kpis) {
    const v = kpis.new_return_rate ?? kpis.new_customer_return_rate;
    if (v != null && !Number.isNaN(Number(v))) {
      return { value: Number(v), month: selectedMonth || null, fromHistory: false };
    }
  }
  return resolveNewReturnFromHistory(kpiHistory, selectedMonth);
}

/** True if we can ground proposals (loading counts as "not missing yet"). */
export function hasUsablePosSnapshot(kpis, kpiHistory, loading) {
  if (loading?.kpis || loading?.kpiHistory) return true;
  if (kpis && typeof kpis === 'object') {
    const nrr = kpis.new_return_rate ?? kpis.new_customer_return_rate;
    if (
      kpis.total_sales != null ||
      kpis.customer_count != null ||
      kpis.nomination_rate != null ||
      nrr != null
    ) {
      return true;
    }
  }
  return Array.isArray(kpiHistory) && kpiHistory.length > 0;
}

/** Proposal stance: 標準 / 強気 / 慎重 — lets 再計算 return a real alternative. */
export const STANCE_MULT = { standard: 1, aggressive: 1.5, conservative: 0.6 };
export const STANCE_LABEL = { standard: '標準案', aggressive: '強気案', conservative: '慎重案' };

export function computeSuggestedTarget(current, unit, salesV, monthsV, baseSales, stanceMult = 1) {
  if (current == null || Number.isNaN(current)) return null;
  const ratio = baseSales && salesV ? (salesV / baseSales) * (6 / Math.max(1, monthsV)) : 1;
  const scale = Math.max(0.4, Math.min(2.5, ratio)) * stanceMult;
  if (unit === 'percent') {
    return Math.round(Math.min(95, Math.max(current, current + (40 - current) * 0.3 * scale)));
  }
  if (unit === 'currency') {
    return Math.round((current + current * 0.1 * scale) / 100) * 100;
  }
  return Math.round(current * (1 + 0.05 * scale));
}

function fmtYen(v) {
  return `¥${Math.round(v).toLocaleString('ja-JP')}`;
}

/**
 * Hybrid proposal: deterministic target + grounded why (for 改善目標 card + chat).
 */
export function proposeFocusTarget({
  label,
  current,
  unit,
  salesGoal,
  planMonths,
  baseSales,
  sourceMonth,
  fromHistory,
  trendDetail,
  stance = 'standard',
}) {
  const stanceMult = STANCE_MULT[stance] ?? 1;
  const target = computeSuggestedTarget(current, unit, salesGoal, planMonths, baseSales, stanceMult);
  if (current == null || target == null) {
    return {
      target: null,
      why: `${label}の現状値がホームPOSスナップショットに無いため、数値目標は出せません。データがある月を選ぶか「別の候補を見る」を試してください。`,
      basis: { current: null, target: null, sourceMonth: sourceMonth || null, fromHistory: !!fromHistory },
    };
  }

  const curLabel = unit === 'percent' ? `${current.toFixed(1)}%` : unit === 'currency' ? fmtYen(current) : String(current);
  const tgtLabel = unit === 'percent' ? `${target}%` : unit === 'currency' ? fmtYen(target) : String(target);
  const src =
    fromHistory && sourceMonth
      ? `${sourceMonth}の履歴スナップショット`
      : sourceMonth
        ? `${sourceMonth}のホームPOS`
        : 'ホームPOSスナップショット';
  const gapHint =
    unit === 'percent'
      ? `（+${(target - current).toFixed(1)}pt）`
      : unit === 'currency'
        ? `（+${fmtYen(target - current)}）`
        : '';

  const whyParts = [
    `根拠: ${src}の${label} 現状 ${curLabel} → 改善目標 ${tgtLabel}${gapHint}。`,
    salesGoal != null
      ? `単月売上ゴール ${fmtYen(salesGoal)}・期間 ${planMonths || 6}ヶ月に合わせて到達可能な幅で算出しました。`
      : null,
    stance !== 'standard' ? `案タイプ: ${STANCE_LABEL[stance] || stance}。` : null,
    trendDetail || null,
  ].filter(Boolean);

  return {
    target,
    why: whyParts.join(' '),
    basis: {
      current,
      target,
      sourceMonth: sourceMonth || null,
      fromHistory: !!fromHistory,
      label,
      unit,
    },
  };
}
